import { Spacing } from '@persona-id/design-tokens';
import { useCallback, useMemo } from 'react';
import { WrappedFieldProps } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled from 'styled-components';

import CheckboxBase from 'inquiry/components/CheckboxBase';
import FormError from 'inquiry/components/FormError';
import { containerStyles, labelSSTStyles } from 'inquiry/mixins/form';
import { InputCheckboxGroupComponent } from 'lib/interfaces/ui-components';

export interface OptionType {
  disabled?: boolean;
  text: string;
  value?: string;
  'description-text'?: string;
}

const Container = styled('div')`
  ${containerStyles}
`;

const ItemContainer = styled('fieldset')`
  display: flex;
  flex-direction: column;
`;

const Legend = styled(Markdown).attrs(() => ({ wrapper: 'legend' }))<{
  styles?: InputCheckboxGroupComponent['styles'];
}>`
  ${({ styles }) => labelSSTStyles(styles)}
`;

const LineItem = styled('label')<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  align-items: baseline;
  margin: 0 ${Spacing[2]};

  & + & {
    margin-top: ${Spacing[2]};
  }
`;

export interface ThemedFormCheckboxGroupProps {
  id: string;
  label?: string;
  options: OptionType[];
  onChange?: (value: string[]) => void;
  className?: string;
  styles?: InputCheckboxGroupComponent['styles'];
  disabled?: boolean;
}

const ThemedFormCheckboxGroup = (props: WrappedFieldProps & ThemedFormCheckboxGroupProps) => {
  const {
    className,
    input: { name, value, onChange, ...inputProps },
    meta: { error, submitFailed },
    label,
    id,
    styles,
    options,
    disabled,
    ...passthroughProps
  } = props;

  /**
   * Used to preserve order of changed values without relying on many O(N) calls
   */
  const optionsIndexMap = useMemo(
    () =>
      options.reduce<Record<string, number>>((map, option: OptionType, index) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        map[option.value] = index;
        return map;
      }, {}),
    [options],
  );

  /**
   * Ensures outputted values are always in fixed order, matching options order.
   */
  const handleChange = useCallback(
    (checked: boolean, checkboxValue: string) => {
      const currentValue: string[] = value || [];

      const updatedValue = checked
        ? currentValue.concat(checkboxValue)
        : currentValue.filter((v) => v !== checkboxValue);

      const stablySortedValue = updatedValue
        .map((v) => optionsIndexMap[v])
        .sort((a, b) => a - b)
        .map((index) => options[index].value);

      onChange(stablySortedValue);
    },
    [value, onChange, options, optionsIndexMap],
  );

  const hasError = !!(error && submitFailed);

  return (
    <Container className={className}>
      <ItemContainer
        id={id}
        aria-invalid={hasError}
        aria-errormessage={`${id}--error`}
        aria-describedby={`${id}--error`}
      >
        {label && <Legend styles={styles}>{label}</Legend>}
        {options.map((option, index) => {
          const optionId = `${name}_${index}`;
          const isSelected = value?.includes(option.value) ?? false;

          return (
            <LineItem isSelected={isSelected} htmlFor={optionId} key={option.value}>
              <CheckboxBase
                {...inputProps}
                {...passthroughProps}
                id={optionId}
                disabled={option.disabled ?? disabled}
                labelText={option.text}
                descriptionText={option['description-text']}
                hasError={hasError}
                value={isSelected}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onChange={(checked) => handleChange(checked, option.value)}
                styles={styles}
                // Avoid a redux-form issue where the checkbox change event
                // causes value to be set to a boolean instead of a string[]
                onFocus={() => null}
                onBlur={() => null}
              />
            </LineItem>
          );
        })}
      </ItemContainer>
      <FormError id={`${id}--error`} hasError={hasError} errorMessage={error} styles={styles} />
    </Container>
  );
};

export default ThemedFormCheckboxGroup;
